
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  watch,
  nextTick,
} from "vue";
import axios from "axios";
import { stateList } from "@/core/listas/stateList";
import InputSelect from "@/components/Inputs/InputSelect/InputSelect.vue";
import { useUsuarioStore } from "@/store/usuarioStore";

export default defineComponent({
  name: "MunicipiosUf",
  components: { InputSelect },
  props: {
    modelValue: {
      type: [String, Number],
      required: false,
      default: "",
    },
    isAvailableShow: {
      type: Boolean,
      default: true,
    },
    municipioClass: {
      type: String,
      default: "col-sm-4",
    },
    ufClass: {
      type: String,
      default: "col-sm-2",
    },
    currentUf: {
      type: String,
      default: "",
    },
    ufLabel: {
      type: String,
      default: "",
    },
    municipioLabel: {
      type: String,
      default: "",
    },
    currentMunicipio: {
      type: [String, Number],
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isAvailableShowMunicipio: {
      type: Boolean,
      default: true,
    },
    municipioRequiredWhenUf: {
      type: Boolean,
      default: false,
    },
    disableProfileFilter: {
      type: Boolean,
      default: false,
    },
    disabledUf: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "changeUf"],
  setup(props, { emit }) {
    const usuarioStore = useUsuarioStore();

    const municipios = ref([]);
    const uf = ref(props.currentUf || "");
    const municipio = ref(
      props.modelValue !== "" && props.modelValue != null
        ? isNaN(Number(props.modelValue))
          ? props.modelValue
          : Number(props.modelValue)
        : props.currentMunicipio || ""
    );
    const isLoading = ref(false);

    // Função para carregar os dados do perfil se necessário
    const loadProfileData = async () => {
      if (!usuarioStore.perfilDados && !isLoading.value) {
        isLoading.value = true;
        try {
          await usuarioStore.carregarDadosUsuario();
        } catch (error) {
          console.error("Erro ao carregar dados do perfil:", error);
        } finally {
          isLoading.value = false;
        }
      }
    };

    // Carrega os dados quando o componente é montado (como fallback)
    onMounted(async () => {
      // Como fallback, carrega se ainda não tiver dados
      if (!usuarioStore.estaCarregado) {
        await loadProfileData();
        await nextTick();
      }

      // Após carregar os dados, inicializa o componente
      if (props.currentUf) {
        carregarMunicipios(props.currentUf);
      } else {
        selecionarUfAutomaticamente();
      }
    });

    // Computed reativo que aguarda os dados do perfil
    const profileData = computed(() => {
      return usuarioStore.perfilDados;
    });

    const filteredStateList = computed(() => {
      // Aguarda os dados do perfil estarem disponíveis
      if (profileData.value?.uf && profileData.value.uf.length > 0) {
        const ufsPermitidas = profileData.value.uf;
        return stateList.filter((state) => ufsPermitidas.includes(state.value));
      }
      // Caso contrário, retornar a lista completa
      return stateList;
    });

    const carregarMunicipios = async (event) => {
      const ufValue = event?.target?.value || event;
      if (ufValue) {
        uf.value = ufValue;
        emit("changeUf", ufValue);
        try {
          const response = await axios.get(
            `${process.env.VUE_APP_ADMIN_API}/municipio/listagem/${ufValue}`
          );
          let municipiosList = response.data.dados.map((dado) => ({
            value: dado.id,
            label: dado.nome,
          }));

          // Aguarda os dados do perfil estarem disponíveis antes de filtrar
          if (profileData.value?.municipioId && !props.disableProfileFilter) {
            const municipioId = profileData.value.municipioId;
            municipiosList = municipiosList.filter(
              (m) => m.value === municipioId
            );
            if (municipiosList.length === 1) {
              municipio.value = municipioId;
              emit("update:modelValue", municipio.value);
            }
          }

          municipios.value = municipiosList;
        } catch (error) {
          console.error("Erro ao carregar municípios:", error);
          municipios.value = [];
        }
      } else {
        // Se UF for limpo, também limpa municípios
        uf.value = "";
        emit("changeUf", "");
        municipios.value = [];
        municipio.value = "";
        emit("update:modelValue", municipio.value);
      }
    };

    const onChange = () => {
      return emit("update:modelValue", municipio.value);
    };

    const selecionarUfAutomaticamente = () => {
      // Se tiver apenas uma UF disponível, selecionar automaticamente
      if (filteredStateList.value.length === 1 && !uf.value) {
        uf.value = filteredStateList.value[0].value;
        carregarMunicipios(uf.value);
      }
    };

    // Watchers
    watch(
      () => props.currentUf,
      (value) => {
        if (value && value !== uf.value) {
          uf.value = value;
          carregarMunicipios(value);
        }
      }
    );

    watch(
      () => props.currentMunicipio,
      (value) => {
        if (value) {
          municipio.value = value;
        }
      }
    );

    watch(
      () => props.modelValue,
      (modelValue) => {
        municipio.value = modelValue;
      }
    );

    watch(
      () => props.isAvailableShow,
      (value) => {
        if (!value) {
          municipio.value = "";
          uf.value = "";
        }
      }
    );

    // Watch quando municipioId do perfil carregar
    watch(
      () => profileData.value?.municipioId,
      (novoMunicipioId) => {
        if (novoMunicipioId && uf.value) {
          carregarMunicipios(uf.value);
        }
      },
      { immediate: true }
    );

    watch(
      filteredStateList,
      () => {
        // Só executa quando os dados do perfil estiverem carregados
        if (profileData.value) {
          selecionarUfAutomaticamente();
        }
      },
      { immediate: true }
    );

    watch(uf, (newUf, oldUf) => {
      if (!newUf && oldUf) {
        municipio.value = "";
        emit("update:modelValue", "");
      }
    });

    return {
      usuarioStore,
      municipios,
      uf,
      municipio,
      isLoading,
      filteredStateList,
      carregarMunicipios,
      onChange,
      selecionarUfAutomaticamente,
    };
  },
});
